import React from 'react';
import { AlertTriangle, Trash2, Save, AlertCircle, CheckCircle } from 'lucide-react';
import Modal from './Modal';

const ConfirmDialog = ({
  isOpen = false,
  onClose,
  onConfirm,
  title,
  message,
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  type = 'default',
  loading = false,
  size = 'sm',
  className = ""
}) => {
  // Dialog types with their configurations
  const dialogTypes = {
    default: {
      icon: AlertCircle,
      iconClass: 'bg-blue-100 text-blue-600',
      confirmClass: 'bg-primary-600 hover:bg-primary-700 focus:ring-primary-500',
      title: title || 'Confirm Action'
    },
    danger: {
      icon: AlertTriangle,
      iconClass: 'bg-red-100 text-red-600',
      confirmClass: 'bg-red-600 hover:bg-red-700 focus:ring-red-500',
      title: title || 'Dangerous Action'
    },
    delete: {
      icon: Trash2,
      iconClass: 'bg-red-100 text-red-600',
      confirmClass: 'bg-red-600 hover:bg-red-700 focus:ring-red-500',
      title: title || 'Delete Item',
      confirmText: confirmText || 'Delete',
      message: message || 'Are you sure you want to delete this item? This action cannot be undone.'
    },
    save: {
      icon: Save,
      iconClass: 'bg-green-100 text-green-600',
      confirmClass: 'bg-green-600 hover:bg-green-700 focus:ring-green-500',
      title: title || 'Save Changes',
      confirmText: confirmText || 'Save',
      message: message || 'Do you want to save your changes?'
    },
    success: {
      icon: CheckCircle,
      iconClass: 'bg-green-100 text-green-600',
      confirmClass: 'bg-green-600 hover:bg-green-700 focus:ring-green-500',
      title: title || 'Success'
    },
    warning: {
      icon: AlertTriangle,
      iconClass: 'bg-yellow-100 text-yellow-600',
      confirmClass: 'bg-yellow-600 hover:bg-yellow-700 focus:ring-yellow-500',
      title: title || 'Warning'
    }
  };

  const config = dialogTypes[type] || dialogTypes.default;
  const Icon = config.icon;

  const displayTitle = config.title;
  const displayMessage = config.message || message;
  const displayConfirmText = config.confirmText || confirmText;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size={size}
      className={className}
      closeOnOverlayClick={!loading}
      showCloseButton={false}
    >
      <div className="sm:flex sm:items-start">
        {/* Icon */}
        <div className={`mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full sm:mx-0 sm:h-10 sm:w-10 ${config.iconClass}`}>
          <Icon className="h-6 w-6 sm:h-5 sm:w-5" />
        </div>

        {/* Content */}
        <div className="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left w-full">
          <h3 className="text-base font-semibold leading-6 text-gray-900 mb-2">
            {displayTitle}
          </h3>
          
          {displayMessage && (
            <div className="mt-2">
              <p className="text-sm text-gray-500 leading-relaxed">
                {displayMessage}
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Actions */}
      <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse gap-3">
        <button
          type="button"
          onClick={onConfirm}
          disabled={loading}
          className={`inline-flex w-full justify-center rounded-md px-3 py-2 text-sm font-semibold text-white shadow-sm sm:w-auto disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-offset-2 transition-colors duration-200 ${config.confirmClass}`}
        >
          {loading && (
            <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
            </svg>
          )}
          {displayConfirmText}
        </button>
        
        <button
          type="button"
          onClick={onClose}
          disabled={loading}
          className="mt-3 inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:mt-0 sm:w-auto disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 transition-colors duration-200"
        >
          {cancelText}
        </button>
      </div>
    </Modal>
  );
};

// Utility hook for confirm dialogs
export const useConfirmDialog = () => {
  const [dialog, setDialog] = React.useState({
    isOpen: false,
    config: {}
  });

  const showConfirm = (config) => {
    return new Promise((resolve) => {
      setDialog({
        isOpen: true,
        config: {
          ...config,
          onConfirm: () => {
            setDialog({ isOpen: false, config: {} });
            resolve(true);
          },
          onClose: () => {
            setDialog({ isOpen: false, config: {} });
            resolve(false);
          }
        }
      });
    });
  };

  const confirmDelete = (itemName = 'item') => {
    return showConfirm({
      type: 'delete',
      title: 'Delete Item',
      message: `Are you sure you want to delete "${itemName}"? This action cannot be undone.`,
      confirmText: 'Delete'
    });
  };

  const confirmSave = () => {
    return showConfirm({
      type: 'save',
      title: 'Save Changes',
      message: 'Do you want to save your changes?',
      confirmText: 'Save'
    });
  };

  const confirmDiscard = () => {
    return showConfirm({
      type: 'warning',
      title: 'Discard Changes',
      message: 'You have unsaved changes. Are you sure you want to discard them?',
      confirmText: 'Discard'
    });
  };

  const ConfirmDialogComponent = () => (
    <ConfirmDialog
      isOpen={dialog.isOpen}
      {...dialog.config}
    />
  );

  return {
    showConfirm,
    confirmDelete,
    confirmSave,
    confirmDiscard,
    ConfirmDialog: ConfirmDialogComponent
  };
};

export default ConfirmDialog;